import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, RotateCcw, Wrench } from 'lucide-react';
import * as copilotoApi from '@/api/copiloto.api';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { enlaceAPantalla } from '@/lib/mapa-sistema';
import { esAvisoSinAcceso } from '@/lib/copiloto-acceso';
import { cn } from '@/lib/cn';
import { useGuia } from '@/components/guia/guia-contexto';

/**
 * B5 · LA VENTANITA DEL COPILOTO.
 *
 * El mismo asistente que contesta por WhatsApp, en una esquina del panel. El
 * hilo es UNO SOLO: se abre mostrando lo último que se habló, haya sido por acá
 * o por el teléfono.
 *
 * LOS BOTONES son lo que la diferencia de un chat cualquiera. "Eso lo ves en
 * Trabajos" no sirve si hay que buscar Trabajos y filtrarlo a mano; el botón
 * deja a la persona parada exactamente donde está la respuesta.
 *
 * El destino lo arma `enlaceAPantalla`, que descarta lo que la pantalla no
 * acepta. Si devolviera `null` —ruta que ya no existe— el botón NO se dibuja:
 * un botón muerto rompe la confianza en todo lo que el copiloto acaba de decir.
 */

interface Burbuja {
  quien: 'yo' | 'copiloto';
  texto: string;
  botones?: copilotoApi.BotonCopiloto[];
}

/**
 * Atajos: lo que la gente pregunta todos los días, a un toque.
 *
 * No son comandos: son el texto que se manda tal cual, como si lo hubiera
 * escrito la persona. Su valor real no es ahorrar tipeo — es **enseñar qué se
 * le puede pedir**. Un chat en blanco no dice qué sabe hacer el que está del
 * otro lado, y la primera pregunta es la más difícil.
 */
const ATAJOS = [
  '¿Cómo va el día?',
  '¿Qué hay que instalar mañana?',
  '¿Qué le prometimos a los clientes?',
  '¿Qué garantías hay abiertas?',
  '¿Quién está esperando respuesta?',
];

/** Los atajos mientras todavía se está configurando. Otro momento, otras preguntas. */
const ATAJOS_PUESTA_EN_MARCHA = ['¿Qué te falta para funcionar?', '¿Qué puedes hacer?'];

export function VentanaCopiloto() {
  const [abierta, setAbierta] = useState(false);
  const [borrador, setBorrador] = useState('');
  const [burbujas, setBurbujas] = useState<Burbuja[]>([]);
  // Se recuerda del último turno: mientras se está configurando, la ventanita
  // lo dice y ofrece otros atajos.
  const [configurando, setConfigurando] = useState(false);
  // La última respuesta fue "no tienes acceso": los atajos invitarían a
  // preguntar algo que no se va a contestar.
  const [sinAcceso, setSinAcceso] = useState(false);
  const navigate = useNavigate();
  const guia = useGuia();
  const qc = useQueryClient();
  const finDelHilo = useRef<HTMLDivElement>(null);

  // El hilo solo se pide al abrir: mientras está cerrada no hay nada que
  // mostrar y no tiene sentido consultarlo.
  const hilo = useQuery({
    queryKey: ['copiloto', 'hilo'],
    queryFn: copilotoApi.traerHilo,
    enabled: abierta,
  });

  useEffect(() => {
    if (!hilo.data) return;
    setBurbujas(
      hilo.data.mensajes.map((m) => ({
        quien: m.role === 'assistant' ? ('copiloto' as const) : ('yo' as const),
        texto: m.content,
      })),
    );
  }, [hilo.data]);

  // Siempre al final: en una conversación, lo último es lo que importa.
  useEffect(() => {
    finDelHilo.current?.scrollIntoView({ behavior: 'smooth' });
  }, [burbujas, abierta]);

  const mandar = useMutation({
    mutationFn: copilotoApi.mandarMensaje,
    onSuccess: (r) => {
      setConfigurando(Boolean(r.puestaEnMarcha));
      setSinAcceso(esAvisoSinAcceso(r.texto));
      setBurbujas((prev) => [...prev, { quien: 'copiloto', texto: r.texto, botones: r.botones }]);
    },
    onError: () =>
      // Nunca se queda mudo: una ventanita que no contesta se lee como rota.
      setBurbujas((prev) => [
        ...prev,
        { quien: 'copiloto', texto: 'No pude contestarte ahora. Prueba de nuevo.' },
      ]),
  });

  // "Empezar de nuevo" abre una conversación nueva: la anterior NO se borra,
  // queda en el historial. Es lo mismo que "Conversación nueva" en la pantalla
  // del asistente, y por eso invalida también su lista de hilos.
  const olvidar = useMutation({
    mutationFn: copilotoApi.empezarDeNuevo,
    onSuccess: () => {
      setBurbujas([]);
      setConfigurando(false);
      void qc.invalidateQueries({ queryKey: ['copiloto', 'hilo'] });
      void qc.invalidateQueries({ queryKey: ['copiloto', 'hilos'] });
    },
  });

  /** Manda un texto como si lo hubiera escrito la persona. */
  function mandarTexto(texto: string) {
    if (!texto || mandar.isPending) return;
    // La burbuja propia se pinta al instante: esperar al server para ver lo que
    // uno mismo escribió se siente roto.
    setBurbujas((prev) => [...prev, { quien: 'yo', texto }]);
    setBorrador('');
    mandar.mutate(texto);
  }

  function enviar() {
    const texto = borrador.trim();
    if (!texto || mandar.isPending) return;
    mandarTexto(texto);
  }

  if (!abierta) {
    return (
      <button
        type="button"
        aria-label="Abrir el asistente"
        onClick={() => setAbierta(true)}
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
      >
        <MessageCircle className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Asistente interno"
      className="fixed bottom-5 right-5 z-40 flex h-[32rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-lg border border-surface-border bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
    >
      <header className="flex items-center justify-between border-b border-surface-border px-3 py-2 dark:border-slate-700">
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Asistente</p>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Empezar de nuevo"
            title="Empezar de nuevo"
            onClick={() => olvidar.mutate()}
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Cerrar el asistente"
            onClick={() => setAbierta(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {hilo.isLoading && <Spinner />}

        {!hilo.isLoading && burbujas.length === 0 && (
          <p className="px-1 text-sm text-slate-500">
            Pregúntame lo que quieras del negocio. Si lo que buscas está en una pantalla, te dejo el
            botón para ir directo.
          </p>
        )}

        {burbujas.map((b, i) => (
          <div key={i} className={cn('flex', b.quien === 'yo' ? 'justify-end' : 'justify-start')}>
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                b.quien === 'yo'
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
              )}
            >
              <p className="whitespace-pre-wrap">{b.texto}</p>

              {(b.botones ?? []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(b.botones ?? []).map((boton, j) => {
                    const destino = enlaceAPantalla(boton.ruta, boton.filtros);
                    // Ruta que ya no existe: NO se dibuja. Un botón muerto es
                    // peor que no tener botón.
                    if (!destino) return null;
                    // "Muéstrame": una guía que este rol no puede usar tampoco se dibuja.
                    if (boton.receta && !guia.puedeUsar(boton.receta)) return null;
                    return (
                      <button
                        key={j}
                        type="button"
                        onClick={() => {
                          setAbierta(false);
                          if (boton.receta) guia.iniciar(boton.receta);
                          else navigate(destino);
                        }}
                        className="rounded-full border border-brand-500 px-2.5 py-1 text-xs font-medium text-brand-700 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-slate-700"
                      >
                        {boton.receta ? `Muéstrame: ${boton.etiqueta}` : boton.etiqueta}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}

        {mandar.isPending && (
          <div className="flex justify-start">
            <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500 dark:bg-slate-800">
              Pensando…
            </div>
          </div>
        )}

        <div ref={finDelHilo} />
      </div>

      {configurando && (
        <p className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Todavía me estás poniendo en marcha. Hasta que terminemos no puedo ver los datos del
            negocio.
          </span>
        </p>
      )}

      {/*
        Los atajos enseñan qué se le puede pedir. Un chat en blanco no dice qué
        sabe hacer el que está del otro lado, y la primera pregunta es la más
        difícil. Se ocultan mientras está contestando para no invitar a
        apilar pedidos encima de uno que todavía no volvió.
      */}
      {!mandar.isPending && !sinAcceso && (
        <div className="flex gap-1.5 overflow-x-auto border-t border-surface-border px-2 py-1.5 dark:border-slate-700">
          {(configurando ? ATAJOS_PUESTA_EN_MARCHA : ATAJOS).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => mandarTexto(a)}
              className="shrink-0 rounded-full border border-surface-border px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {a}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 border-t border-surface-border p-2 dark:border-slate-700">
        <input
          value={borrador}
          onChange={(e) => setBorrador(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
          placeholder="Pregúntame algo…"
          aria-label="Escribile al asistente"
          className="min-w-0 flex-1 rounded-md border border-surface-border bg-transparent px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
        />
        <Button size="sm" aria-label="Enviar" onClick={enviar} loading={mandar.isPending}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
